import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowRight,
  BadgeCheck,
  CircleCheck,
  Gift,
  Handshake,
  LifeBuoy,
  Percent,
  Rocket,
  Send,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import { tokens } from '../theme';

const CONTACT_API_URL =
  'https://cleolead-api-743703226990.us-central1.run.app/api/v1/contact';

const benefits = [
  {
    icon: Percent,
    title: '30% recurring commission',
    description:
      'Earn 30% of every subscription you bring — recurring, for as long as the client stays with CleoLead.',
  },
  {
    icon: Gift,
    title: 'Free CleoLead access',
    description:
      'A full-featured workspace for your team to build, test, and demo client solutions at no cost.',
  },
  {
    icon: Handshake,
    title: 'Get matched with clients',
    description:
      'Businesses that want hands-on setup get routed directly to certified Experts like you.',
  },
  {
    icon: LifeBuoy,
    title: 'Priority support',
    description:
      'A private Expert channel with direct access to our team — ahead of the regular queue.',
  },
  {
    icon: Rocket,
    title: 'Early access to features',
    description:
      'Test upcoming channels and capabilities before they ship, and shape the roadmap with us.',
  },
  {
    icon: BadgeCheck,
    title: 'Official Expert badge',
    description:
      'A verified CleoLead Expert badge and a listing in our directory to win client trust.',
  },
];

const steps = [
  {
    number: '01',
    title: 'Apply',
    description:
      'Tell us about your agency, the clients you serve, and your experience with AI automation.',
  },
  {
    number: '02',
    title: 'Get verified',
    description:
      'A short intro call and hands-on onboarding — you get certified and listed as an official Expert.',
  },
  {
    number: '03',
    title: 'Start earning',
    description:
      'Bring your clients or get matched with ours, and collect 30% recurring commission on every one.',
  },
];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: `${tokens.radiusControl}px`,
    bgcolor: tokens.bgPaper,
    '& fieldset': { borderColor: tokens.border },
    '&:hover fieldset': { borderColor: tokens.copper },
    '&.Mui-focused fieldset': { borderColor: tokens.copper },
  },
};

interface ApplicationData {
  name: string;
  email: string;
  company: string;
  website: string;
  experience: string;
}

const emptyApplication: ApplicationData = {
  name: '',
  email: '',
  company: '',
  website: '',
  experience: '',
};

function ApplicationForm() {
  const [formData, setFormData] = useState<ApplicationData>(emptyApplication);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setError('');
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: '' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors: { [key: string]: string } = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!formData.company.trim()) errors.company = 'Company or agency name is required';
    if (!formData.experience.trim()) errors.experience = 'This field is required';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSending(true);
    setError('');

    const message = [
      `Company / agency: ${formData.company.trim()}`,
      `Website: ${formData.website.trim() || '—'}`,
      '',
      'Experience:',
      formData.experience.trim(),
    ].join('\n');

    try {
      const response = await fetch(CONTACT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          subject: `Expert Program Application — ${formData.company.trim()}`,
          message,
          form_type: 'experts',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.error || 'Failed to send application');
      }

      setSending(false);
      setSent(true);
    } catch (err) {
      setSending(false);
      setError(
        err instanceof Error ? err.message : 'Failed to send application. Please try again.',
      );
    }
  };

  if (sent) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Box sx={{ color: 'success.main', mb: 2, display: 'inline-flex' }}>
          <CircleCheck size={64} strokeWidth={1.5} />
        </Box>
        <Typography variant="h5" gutterBottom>
          Application received!
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
          Thanks for applying to the CleoLead Experts program. We review every
          application and will get back to you within 2–3 business days.
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            name="name"
            label="Your Name"
            fullWidth
            required
            value={formData.name}
            onChange={handleChange}
            disabled={sending}
            error={!!fieldErrors.name}
            helperText={fieldErrors.name}
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            name="email"
            label="Email Address"
            type="email"
            fullWidth
            required
            value={formData.email}
            onChange={handleChange}
            disabled={sending}
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            name="company"
            label="Company or Agency Name"
            fullWidth
            required
            value={formData.company}
            onChange={handleChange}
            disabled={sending}
            error={!!fieldErrors.company}
            helperText={fieldErrors.company}
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            name="website"
            label="Website (Optional)"
            fullWidth
            value={formData.website}
            onChange={handleChange}
            disabled={sending}
            placeholder="https://youragency.com"
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            name="experience"
            label="Tell us about your experience"
            multiline
            rows={5}
            fullWidth
            required
            value={formData.experience}
            onChange={handleChange}
            disabled={sending}
            placeholder="What clients do you serve? Have you built AI automations, chatbots, or CRM workflows before?"
            error={!!fieldErrors.experience}
            helperText={fieldErrors.experience}
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={sending}
            startIcon={
              sending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Send size={18} strokeWidth={1.5} />
              )
            }
            sx={{ px: 4, py: 1.5 }}
          >
            {sending ? 'Sending...' : 'Submit Application'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

export default function ExpertsPage() {
  const scrollToApply = () => {
    document.getElementById('apply')?.scrollIntoView?.({ behavior: 'smooth' });
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Navbar />

      {/* Hero */}
      <Box sx={{ pt: { xs: 8, md: 12 }, pb: { xs: 8, md: 10 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Reveal>
            <Typography variant="overline" component="p" sx={{ mb: 2 }}>
              CleoLead Experts Program
            </Typography>
            <Typography variant="h1" component="h1" sx={{ mb: 3 }}>
              Become a CleoLead Expert
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontSize: '1.15rem',
                maxWidth: 560,
                mx: 'auto',
                mb: 4,
              }}
            >
              For agencies, consultants, and freelancers who build AI-powered
              customer engagement for their clients with CleoLead.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                variant="contained"
                color="primary"
                size="large"
                endIcon={<ArrowRight size={18} strokeWidth={1.5} />}
                onClick={scrollToApply}
                sx={{ px: 4, py: 1.6, fontSize: '1.05rem' }}
              >
                Apply to join
              </Button>
            </Stack>
          </Reveal>
        </Container>
      </Box>

      {/* Benefits */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: tokens.bandAlt,
          borderTop: `1px solid ${tokens.border}`,
          borderBottom: `1px solid ${tokens.border}`,
        }}
      >
        <Container maxWidth="lg">
          <Reveal sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography variant="overline" component="p" sx={{ mb: 1.5 }}>
              Why partner with us
            </Typography>
            <Typography variant="h2">
              Turn AI automation into recurring revenue
            </Typography>
          </Reveal>
          <Grid container spacing={3}>
            {benefits.map((benefit, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={benefit.title}>
                <Reveal delay={index * 70} sx={{ height: '100%' }}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3.5 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: `${tokens.radiusControl}px`,
                          bgcolor: tokens.copperTint,
                          color: tokens.copper,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                        }}
                      >
                        <benefit.icon size={22} strokeWidth={1.5} />
                      </Box>
                      <Typography variant="h5" sx={{ mb: 1 }}>
                        {benefit.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {benefit.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How it works */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Reveal sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography variant="overline" component="p" sx={{ mb: 1.5 }}>
              How it works
            </Typography>
            <Typography variant="h2">From application to first commission</Typography>
          </Reveal>
          <Grid container spacing={4}>
            {steps.map((step, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={step.title}>
                <Reveal delay={index * 70}>
                  <Typography
                    sx={{
                      fontFamily: tokens.fontDisplay,
                      fontSize: '2.4rem',
                      fontWeight: 600,
                      color: tokens.copper,
                      lineHeight: 1,
                      mb: 1.5,
                    }}
                  >
                    {step.number}
                  </Typography>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Apply */}
      <Box
        id="apply"
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: tokens.bandAlt,
          borderTop: `1px solid ${tokens.border}`,
        }}
      >
        <Container maxWidth="md">
          <Reveal sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
            <Typography variant="overline" component="p" sx={{ mb: 1.5 }}>
              Apply now
            </Typography>
            <Typography variant="h2" sx={{ mb: 2 }}>
              Join the Experts program
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', maxWidth: 520, mx: 'auto' }}
            >
              Tell us about you and your clients. We review every application
              personally and reply within 2–3 business days.
            </Typography>
          </Reveal>
          <Reveal delay={70}>
            <Paper
              elevation={1}
              sx={{ p: { xs: 3, md: 5 }, borderRadius: `${tokens.radiusCard}px` }}
            >
              <ApplicationForm />
            </Paper>
          </Reveal>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
